/**
 * Alert - Contextual feedback message
 *
 * Usage:
 * <Alert variant="info" title="Note">This is informational.</Alert>
 * <Alert variant="error">Something went wrong!</Alert>
 * <Alert variant="success" dismissible onDismiss={() => setShow(false)}>Saved!</Alert>
 */

import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';

import { cn } from '../utils';

export interface AlertProps {
  /** Alert content */
  children: React.ReactNode;
  /** Color variant */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /** Optional title */
  title?: string;
  /** Show dismiss button */
  dismissible?: boolean;
  /** Callback when dismissed */
  onDismiss?: () => void;
  /** Additional classes */
  className?: string;
}

const variantStyles = {
  info: {
    container: 'bg-blue-500/10 border-blue-500/20',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-600 dark:text-blue-400',
    text: 'text-blue-700 dark:text-blue-300',
  },
  success: {
    container: 'bg-green-500/10 border-green-500/20',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-600 dark:text-green-400',
    text: 'text-green-700 dark:text-green-300',
  },
  warning: {
    container: 'bg-yellow-500/10 border-yellow-500/20',
    icon: 'text-yellow-600 dark:text-yellow-400',
    title: 'text-yellow-600 dark:text-yellow-400',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  error: {
    container: 'bg-red-500/10 border-red-500/20',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-600 dark:text-red-400',
    text: 'text-red-700 dark:text-red-300',
  },
};

const icons = {
  info: Info,
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
};

export function Alert({
  children,
  variant = 'info',
  title,
  dismissible,
  onDismiss,
  className,
}: AlertProps) {
  const styles = variantStyles[variant];
  const Icon = icons[variant];

  return (
    <div
      role="alert"
      className={cn('relative flex gap-3 rounded-lg border p-4', styles.container, className)}
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0', styles.icon)} />
      <div className="flex-1">
        {title && <h5 className={cn('font-medium', styles.title)}>{title}</h5>}
        <div className={cn('text-sm', title && 'mt-1', styles.text)}>{children}</div>
      </div>
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className={cn('flex-shrink-0 hover:opacity-75 transition-opacity', styles.icon)}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
